import { useMemo } from "react";
import { skipToken } from "@tanstack/react-query";
import type { RouterInputs } from "@remi/api";
import { NotificationType } from "@remi/enums/notification/type";
import { can } from "@remi/permissions";
import type { GroupedNotificationListItem, NotificationListItem } from "@remi/types/notification/list";
import { Comparator, MySqlValue } from "@remi/types/query-builder";

import { api } from "~/utils/api";
import { useUserProfile } from "~/contexts/UserProfileContext";

type UseNotificationsProps = RouterInputs["communication"]["searchNotifications"];

export const useNotifications = (props: UseNotificationsProps) => {
  const { user } = useUserProfile();
  const canRead = can.notificationList.read(user);
  const { data: rawNotifications = [], ...queryProps } = api.communication.searchNotifications.useQuery(
    canRead ? props : skipToken,
  );

  const processedNotifications = useMemo(() => {
    const grouped = groupNotifications(rawNotifications);
    return grouped;
  }, [rawNotifications]);

  return {
    ...queryProps,
    data: processedNotifications,
  };
};

const getGroupKey = (notification: NotificationListItem): string => {
  switch (notification.type) {
    case NotificationType.CONVERSATION_ITEM_MENTION:
      return `CONVERSATION_ITEM_MENTION_${notification.conversationItem.id}`;
    case NotificationType.CONVERSATION_ITEM_REPLY:
      return `CONVERSATION_ITEM_${notification.conversationItem.parent?.id}`;
    case NotificationType.CONVERSATION_ITEM_GROUP_MENTION:
      return `CONVERSATION_ITEM_GROUP_MENTION_${notification.conversationItem.id}`;
    case NotificationType.CONVERSATION_ITEM_ORGANIZATION_MENTION:
      return `CONVERSATION_ITEM_ORGANIZATION_MENTION_${notification.conversationItem.id}`;
    case NotificationType.CONVERSATION_ITEM_REACTION:
      return `CONVERSATION_ITEM_${notification.conversationItem.parent?.id}`;
    case NotificationType.CONVERSATION_ITEM_CONVERSATION_SUBSCRIBED:
      return `CONVERSATION_ITEM_${notification.conversationItem.parent?.id ?? notification.conversationItem.conversation.id}`;
    case NotificationType.TICKET_SUBSCRIBED_STATUS_CHANGE:
      return `TICKET_SUBSCRIBED_STATUS_CHANGE_${notification.ticketHistory.id}`;
    case NotificationType.TICKET_SUBSCRIBED_URGENT_PRIORITY_CHANGE:
      return `TICKET_SUBSCRIBED_URGENT_PRIORITY_CHANGE_${notification.ticketHistory.id}`;
    case NotificationType.TICKET_ASSIGNED_URGENT_ASSIGNMENT_CHANGE:
      return `TICKET_ASSIGNED_URGENT_ASSIGNMENT_CHANGE_${notification.ticketHistory.id}`;
    case NotificationType.TICKET_ASSIGNED_ASSIGNMENT_CHANGE:
      return `TICKET_ASSIGNED_ASSIGNMENT_CHANGE_${notification.ticketHistory.id}`;
    case NotificationType.TICKET_SUBSCRIBED_SNOOZED:
      return `TICKET_SUBSCRIBED_SNOOZED_${notification.ticketHistory.id}`;
    case NotificationType.TICKET_SUBSCRIBED_URGENT_SNOOZED:
      return `TICKET_SUBSCRIBED_URGENT_SNOOZED_${notification.ticketHistory.id}`;
    case NotificationType.ESCROW_STATUS_CHANGE:
      return `ESCROW_STATUS_CHANGE_${notification.escrowHistory.id}`;
    case NotificationType.FINANCING_STATUS_CHANGE:
      return `FINANCING_STATUS_CHANGE_${notification.financingHistory.id}`;
    case NotificationType.DEALER_FEE_UNKNOWN:
      return `DEALER_FEE_UNKNOWN_${notification.financingHistory.id}`;
    case NotificationType.HIC_STATUS_CHANGE:
      return `HIC_STATUS_CHANGE_${notification.pandadocDocumentHistory.id}`;
    case NotificationType.ESCALATION_SUBSCRIBED_STATUS_CHANGE:
      return `ESCALATION_STATUS_CHANGE_${notification.escalationHistory.id}`;
    case NotificationType.ESCALATION_SUBSCRIBED_RISK_TIER_CHANGE:
      return `ESCALATION_RISK_TIER_CHANGE_${notification.escalationHistory.id}`;
    case NotificationType.ESCALATION_ASSIGNED_ASSIGNMENT_CHANGE:
      return `ESCALATION_ASSIGNMENT_CHANGE_${notification.escalationHistory.id}`;
    case NotificationType.NEW_WORK_ORDER:
      return `NEW_WORK_ORDER_${notification.id}`;
  }
};

const groupNotifications = (
  notifications: NotificationListItem[],
): (GroupedNotificationListItem | NotificationListItem)[] => {
  if (notifications.length === 0) {
    return [];
  }

  const result: (GroupedNotificationListItem | NotificationListItem)[] = [];
  const groupMap = new Map<string, GroupedNotificationListItem>();

  for (const notification of notifications) {
    const shouldGroup = notification.readAt === null && notification.deletedAt === null;

    if (!shouldGroup) {
      result.push(notification);
      continue;
    }

    const groupKey = getGroupKey(notification);
    const existing = groupMap.get(groupKey);

    if (existing) {
      existing.notifications.push(notification);
    } else {
      const newGroup: GroupedNotificationListItem = {
        id: notification.id,
        notifications: [notification],
      };
      groupMap.set(groupKey, newGroup);
      result.push(newGroup);
    }
  }

  return result.map((item) => {
    if (isGroupedNotification(item) && item.notifications.length === 1 && item.notifications[0] !== undefined) {
      return item.notifications[0];
    }
    return item;
  });
};

export const isGroupedNotification = (
  item: GroupedNotificationListItem | NotificationListItem,
): item is GroupedNotificationListItem => {
  return "notifications" in item;
};

export const isNotification = (
  item: GroupedNotificationListItem | NotificationListItem,
): item is NotificationListItem => {
  return "type" in item;
};

/**
 * Resolves selected list-item IDs to the actual notification IDs to send to the API.
 * For grouped items, expands to all notification IDs in the group so "mark all as read" updates every notification.
 */
export const getNotificationIdsFromSelection = (
  notifications: (GroupedNotificationListItem | NotificationListItem)[],
  selectedIds: Set<string>,
): string[] => {
  const ids: string[] = [];
  for (const item of notifications) {
    if (!selectedIds.has(item.id)) {
      continue;
    }
    if (isGroupedNotification(item)) {
      for (const n of item.notifications) {
        ids.push(n.id);
      }
    } else {
      ids.push(item.id);
    }
  }
  return ids;
};

export const DefaultNotificationsDefinition = {
  definition: {
    operator: "AND" as const,
    conditions: [
      {
        field: "readAt" as const,
        comparator: Comparator.EQUALS,
        value: MySqlValue.NULL,
      },
      {
        field: "deletedAt" as const,
        comparator: Comparator.EQUALS,
        value: MySqlValue.NULL,
      },
    ],
  },
  sort: {
    sort: [
      {
        field: "createdAt" as const,
        direction: "DESC" as const,
      },
    ],
  },
};
